import React, { useState } from "react";
import toast from "react-hot-toast";
import { exportRecruiterInterviewReport } from "../services/jobService";
import "./RecruiterJobs.css";

function ScoreBlock({ label, value, summary }) {
  return (
    <div className="report-score-block">
      <span>{label}</span>
      <strong>{value ?? 0}/100</strong>
      {summary && <p>{summary}</p>}
    </div>
  );
}

function TextList({ title, items }) {
  const values = Array.isArray(items) ? items.filter(Boolean) : [];
  return (
    <section className="report-section">
      <h3>{title}</h3>
      {values.length ? (
        <ul>
          {values.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}
        </ul>
      ) : (
        <p>No notes available.</p>
      )}
    </section>
  );
}

export default function RecruiterReportDetail({ report, pending, error, loading, onClose }) {
  const [exporting, setExporting] = useState(false);

  if (!report && !pending && !loading && !error) return null;

  const handleExport = async () => {
    if (!report?.jobId || !report?.interviewId) return;
    setExporting(true);
    try {
      const { blob, filename } = await exportRecruiterInterviewReport(report.jobId, report.interviewId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Report exported");
    } catch (err) {
      toast.error(err.message || "Unable to export report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="report-detail-panel">
      <div className="report-detail-header">
        <div>
          <h2>Interview Report</h2>
          <p>{report?.candidateName || "Candidate"} {report?.jobTitle ? `- ${report.jobTitle}` : ""}</p>
        </div>
        <div className="report-actions">
          <button type="button" className="secondary-button" onClick={handleExport} disabled={!report || exporting}>
            {exporting ? "Exporting..." : "Export PDF"}
          </button>
          <button type="button" className="link-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {loading ? (
        <div className="recruiter-empty compact">Loading report...</div>
      ) : pending ? (
        <div className="recruiter-empty compact">Report generation is still in progress.</div>
      ) : error ? (
        <div className="recruiter-alert">{error}</div>
      ) : (
        <>
          <div className="report-score-grid">
            <ScoreBlock label="Overall Score" value={report.overallScore} />
            <ScoreBlock label="Technical Skills" value={report.technicalSkills?.score} summary={report.technicalSkills?.summary} />
            <ScoreBlock label="Communication" value={report.communication?.score} summary={report.communication?.summary} />
            <ScoreBlock label="Confidence" value={report.confidence?.score} summary={report.confidence?.summary} />
            <ScoreBlock label="Integrity Score" value={report.integrity?.score} summary={report.integrity?.level} />
          </div>

          <section className="report-section">
            <h3>Interview Summary</h3>
            <p>{report.interviewSummary || "No summary available."}</p>
            <p className="report-muted">
              Mode used: {report.uploadedQuestionModeUsed || report.interviewMode || "N/A"}
              {report.uploadedQuestionsCount !== undefined ? ` | Uploaded questions: ${report.uploadedQuestionsCount}` : ""}
            </p>
            {report.totalQuestions ? (
              <p className="report-muted">
                Questions Answered: {report.questionsAnswered ?? 0}/{report.totalQuestions}
                {` (${Math.round(((report.questionsAnswered ?? 0) / report.totalQuestions) * 100)}%)`}
              </p>
            ) : null}
          </section>

          <div className="report-columns">
            <TextList title="Strengths" items={report.strengths} />
            <TextList title="Observed Gaps" items={report.weaknesses} />
          </div>

          <section className="report-section">
            <h3>Question / Answer Highlights</h3>
            {(report.questionAnswerHighlights || []).length ? (
              <div className="qa-highlight-list">
                {report.questionAnswerHighlights.map((item, index) => (
                  <article key={`qa-${index}`}>
                    <strong>Q: {item.question || "Question not available"}</strong>
                    <p>A: {item.answer || "Answer not available"}</p>
                    {item.score !== undefined && item.score !== null && (
                      <p>Answer correctness: {item.score}/100</p>
                    )}
                    {item.feedback && <p>Recruiter assessment: {item.feedback}</p>}
                  </article>
                ))}
              </div>
            ) : (
              <p>No highlights available.</p>
            )}
          </section>

          <section className="report-section">
            <h3>Integrity Signals</h3>
            <div className="integrity-signal-grid">
              {Object.entries(report.integrity?.signals || {}).map(([key, value]) => (
                <span key={key}>{key}: <strong>{String(value)}</strong></span>
              ))}
            </div>
          </section>

          <section className="report-section">
            <h3>Transcript Snippets</h3>
            <div className="transcript-snippets">
              {(report.transcriptSnippets || []).map((snippet, index) => (
                <p key={`snippet-${index}`}>
                  <strong>{snippet.speaker}:</strong> {snippet.text}
                </p>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
